import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api';
import { useCaminho } from '../../Caminho';
import { data, dataDoDia, dataHora, nomeDoContato, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { degrauDe, ESTAGIO_DA_OPORTUNIDADE, SITUACAO_DA_TAREFA, SITUACAO_DO_ATENDIMENTO } from '../../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import { Carregando, ErroDaApi, LinkProtegido, RespostaDaAcao, Selo } from '../../ui';
import {
  CANAL_DE_ENVIO, EVENTO_DO_FUNIL, LINHA, MOTIVO_DA_PERDA, ORIGEM, TIPO_DE_TAREFA, localDaObra, nomeDaProposta, reais,
  textoDe, type DetalheDaOportunidade, type Movimento, type Tarefa,
} from './crm';
import {
  JanelaCancelarTarefa, JanelaDaOportunidade, JanelaDaTarefa, JanelaMover, JanelaProposta,
} from './JanelasDaOportunidade';
import './oportunidades.css';

/**
 * A oportunidade — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa 15; spec
 * do Comercial §5.1, §8.3 e §9.7). `/comercial/oportunidade/:id`.
 *
 * O pedido inteiro numa tela: quem pediu e o quê (o contato, o cliente, a
 * linha, o local, o escopo, a estimativa), em que pé está (o estágio, a
 * próxima ação), as propostas feitas em Word com o PDF, as tarefas, as
 * conversas e o HISTÓRICO do cartão — cada movimento com a origem por
 * extenso («Sistema», «Agente» ou o nome da pessoa) e o motivo ou o evento
 * que o causou (RN-471 · RN-C02).
 *
 * As ações: «Registrar proposta» é a primária (o que a Etapa 1 pede da
 * oportunidade, §12); «Mover para…» e «Mudar» são secundárias. Quem vê a
 * oportunidade por uma conversa, mas não cuida dela, lê a frase de quem
 * cuida no lugar dos botões (M16). Os valores (estimado e das propostas) são
 * de quem tem o Comercial (`crm.ver`; decisão do Humberto, 22/10) — e só quem
 * o tem chega a esta tela.
 *
 * Ao vivo: `comercial.oportunidade.<id>` (só ids; a API confere a carteira a
 * cada evento).
 */

/** Um par rótulo/valor; o valor ausente dito por extenso (P-TXT-02). */
function Par({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="op-par">
      <dt>{rotulo}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * A origem do movimento por extenso: «Sistema», «Agente» ou a pessoa. O
 * movimento do sistema com nome é o que nasceu do que a pessoa fez: o
 * nascimento da criada à mão, ou um evento do funil na sessão dela (a
 * proposta registrada, a linha e o local identificados; Tarefa 17).
 */
export function quemMoveu(m: Movimento): string {
  if (m.origem === 'pessoa') return m.quem?.nome ?? 'Uma pessoa';
  if (m.origem === 'agente') return 'Agente';
  if (!m.quem) return 'Sistema';
  return m.evento === 'contato_novo' ? `Sistema · criada por ${m.quem.nome}` : `Sistema · depois de ${m.quem.nome}`;
}

function ItemDoHistorico({ m }: { m: Movimento }) {
  const para = degrauDe(ESTAGIO_DA_OPORTUNIDADE, m.para);
  const de = m.de ? degrauDe(ESTAGIO_DA_OPORTUNIDADE, m.de).texto : null;
  return (
    <li className={`historico-item origem-${m.origem}`}>
      <div className="historico-topo">
        <Selo tom={para.tom}>{para.texto}</Selo>
        {de && <span className="op-aux">de {de}</span>}
        <time className="op-aux" dateTime={m.em}>{dataHora(m.em)}</time>
      </div>
      <p className="historico-quem">{quemMoveu(m)}</p>
      <p className="historico-porque">
        {m.motivo ?? (m.evento ? textoDe(EVENTO_DO_FUNIL, m.evento) : 'sem motivo registrado')}
      </p>
    </li>
  );
}

function LinhaDaTarefa({ t, podeMudar, aoFeita, aoCancelar }: {
  t: Tarefa; podeMudar: boolean; aoFeita(): void; aoCancelar(): void;
}) {
  const sit = degrauDe(SITUACAO_DA_TAREFA, t.vencida ? 'vencida' : t.situacao);
  return (
    <li className="op-tarefa">
      <div className="op-tarefa-texto">
        <span className="op-tarefa-titulo">{t.titulo}</span>
        <span className="op-aux">
          {textoDe(TIPO_DE_TAREFA, t.tipo)} · {t.situacao === 'feita' && t.feitaEm ? `feita em ${dataHora(t.feitaEm)}`
            : `vence ${dataHora(t.venceEm)}`} · {t.responsavel.tipo === 'agente' ? 'o agente' : t.responsavel.nome}
        </span>
        {t.motivoCancelamento && <span className="op-aux">Cancelada: {t.motivoCancelamento}</span>}
      </div>
      <Selo tom={sit.tom}>{sit.texto}</Selo>
      {podeMudar && t.situacao === 'aberta' && (
        <span className="op-tarefa-acoes">
          <button type="button" className="botao secundario" onClick={aoFeita}>Feita</button>
          <button type="button" className="botao discreto" onClick={aoCancelar}>Cancelar…</button>
        </span>
      )}
    </li>
  );
}

export function Oportunidade() {
  const { id = '' } = useParams<{ id: string }>();
  const { pode } = useSessao();
  const [d, setD] = useState<DetalheDaOportunidade | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [janela, setJanela] = useState<null | 'mover' | 'proposta' | 'mudar' | 'tarefa'>(null);
  const [cancelando, setCancelando] = useState<Tarefa | null>(null);
  const pedido = useRef(0);

  useCaminho(d ? [{ rotulo: nomeDoContato(d.oportunidade.contato.nome) }] : null);

  const buscar = useCallback(async () => {
    const n = ++pedido.current;
    try {
      const r = await api<DetalheDaOportunidade>(`/comercial/oportunidades/${id}`);
      if (n !== pedido.current) return;
      setD(r);
      setErro(null);
    } catch (e) {
      if (n === pedido.current) setErro(e);
    }
  }, [id]);

  useEffect(() => { setD(null); void buscar(); }, [buscar]);

  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal([`comercial.oportunidade.${id}`], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void buscar(); }, 150);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  const feito = (texto: string) => { setJanela(null); setCancelando(null); setAcaoErro(null); setOk(texto); void buscar(); };

  const marcarFeita = async (t: Tarefa) => {
    setOk(null);
    try {
      await api(`/comercial/tarefas/${t.id}`, { metodo: 'PATCH', corpo: { situacao: 'feita' } });
      feito('Tarefa feita.');
    } catch (e) { setAcaoErro(e); }
  };

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Carregando a oportunidade" />;

  const o = d.oportunidade;
  const estagio = degrauDe(ESTAGIO_DA_OPORTUNIDADE, o.estagio);
  const local = localDaObra(o.local);
  const edita = o.podeEditar;
  const vePeloOutro = pode('crm.editar') && !edita;

  return (
    <div className="op-tela">
      <header className="cabeca op-cabeca">
        <div className="cresce">
          <h1>{nomeDoContato(o.contato.nome)}</h1>
          <p className="sub">
            {[o.cliente?.nome ?? 'ainda sem cliente', o.linha ? textoDe(LINHA, o.linha) : null, local || null]
              .filter(Boolean).join(' · ')}
          </p>
          <p className="op-selos">
            <Selo tom={estagio.tom}>{estagio.texto}</Selo>
            {o.cliente?.estrategico && <Selo tom="andamento">Cliente estratégico</Selo>}
            <span className="op-aux">desde {data(o.estagioDesde)} · {textoDe(ORIGEM, o.origem)}</span>
          </p>
        </div>
        {edita && (
          <div className="op-acoes">
            <button type="button" className="botao secundario" onClick={() => setJanela('mover')}>Mover para…</button>
            <button type="button" className="botao secundario" onClick={() => setJanela('mudar')}>Mudar</button>
            <button type="button" className="botao" onClick={() => setJanela('proposta')}>Registrar proposta</button>
          </div>
        )}
      </header>
      {vePeloOutro && (
        <p className="op-quem-cuida">
          {o.responsavel?.nome ?? 'Outra pessoa'} cuida desta oportunidade. Você a vê pela conversa; para mudá-la,
          peça a {o.responsavel?.nome ?? 'quem cuida dela'} ou à diretoria.
        </p>
      )}
      <AtualizacaoPausada estado={tempoReal} />
      <RespostaDaAcao erro={acaoErro} ok={ok} />

      <div className="op-grade">
        <div className="op-coluna">
          <section className="cartao op-cartao">
            <div className="cartao-cabeca"><h2>O pedido</h2></div>
            <dl className="op-pares">
              <Par rotulo="Contato"><Link to={`/comercial/contato/${o.contato.id}`}>{nomeDoContato(o.contato.nome)}</Link></Par>
              <Par rotulo="Cliente">{o.cliente?.nome ?? 'ainda sem cliente — o contato é um pedido novo'}</Par>
              <Par rotulo="Linha">{o.linha ? textoDe(LINHA, o.linha) : 'ainda não identificada'}</Par>
              <Par rotulo="Local da obra">{local || 'ainda não informado'}</Par>
              <Par rotulo="Escopo">{o.escopo || 'ainda não descrito'}</Par>
              {o.furos != null && <Par rotulo="Furos">{plural(o.furos, 'furo', 'furos')}</Par>}
              <Par rotulo="Estimativa">
                {o.valorEstimado === undefined
                  ? <span className="op-aux">o valor é de quem tem o acesso do Comercial</span>
                  : o.valorEstimado === null ? 'ainda sem estimativa'
                    : <>{reais(o.valorEstimado)}{o.valorPelaRegra && <span className="op-aux"> · pela regra simples</span>}</>}
              </Par>
              <Par rotulo="Responsável">{o.responsavel?.nome ?? 'ninguém ainda'}</Par>
              {o.indicadaPor && <Par rotulo="Indicada por">{o.indicadaPor.nome}</Par>}
              <Par rotulo="Próxima ação">
                {o.proximaAcao
                  ? <>{o.proximaAcao}{o.proximaAcaoEm && <span className="op-aux"> · {dataHora(o.proximaAcaoEm)}</span>}</>
                  : 'nenhuma combinada'}
              </Par>
              <Par rotulo="Acompanhamento">{o.acompanhamentoPessoal ? 'pessoal, do responsável' : 'automático'}</Par>
              {o.motivoPerda && (
                <Par rotulo="Motivo da perda">
                  {textoDe(MOTIVO_DA_PERDA, o.motivoPerda)}{o.motivoPerdaTexto && <span className="op-aux"> · {o.motivoPerdaTexto}</span>}
                </Par>
              )}
            </dl>
          </section>

          <section className="cartao op-cartao">
            <div className="cartao-cabeca">
              <h2>Propostas</h2>
              <span className="sub">{plural(d.propostas.length, 'registrada', 'registradas')}</span>
            </div>
            {d.propostas.length === 0 ? (
              <p className="op-vazio">
                Nenhuma proposta registrada. A proposta feita em Word entra aqui com o PDF, para o acompanhamento e
                o painel funcionarem.
              </p>
            ) : (
              <ul className="op-propostas">
                {d.propostas.map((p) => (
                  <li key={p.id}>
                    <span className="op-proposta-numero mono">{nomeDaProposta(p.numero, p.revisao)}</span>
                    <span className="op-aux">
                      enviada em {data(p.enviadaEm)} por {textoDe(CANAL_DE_ENVIO, p.canalEnvio)} · vale até {dataDoDia(p.validade)}
                    </span>
                    <span className="op-proposta-valor">
                      {p.valor === undefined ? <span className="op-aux">valor do Comercial</span> : reais(p.valor)}
                    </span>
                    <LinkProtegido caminho={`/comercial/propostas/${p.id}/pdf`} nomeQueda={`${nomeDaProposta(p.numero, p.revisao)}.pdf`}>
                      Abrir o PDF
                    </LinkProtegido>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="cartao op-cartao">
            <div className="cartao-cabeca">
              <h2>Tarefas</h2>
              {pode('crm.editar') && (
                <button type="button" className="botao secundario" onClick={() => setJanela('tarefa')}>Nova tarefa</button>
              )}
            </div>
            {d.tarefas.length === 0 ? (
              <p className="op-vazio">Nenhuma tarefa. O acompanhamento automático da proposta cria as dele.</p>
            ) : (
              <ul className="op-tarefas">
                {d.tarefas.map((t) => (
                  <LinhaDaTarefa key={t.id} t={t} podeMudar={pode('crm.editar')}
                    aoFeita={() => void marcarFeita(t)} aoCancelar={() => setCancelando(t)} />
                ))}
              </ul>
            )}
          </section>

          <section className="cartao op-cartao">
            <div className="cartao-cabeca"><h2>Conversas</h2></div>
            {d.conversas.length === 0 ? (
              <p className="op-vazio">Nenhuma conversa que você veja. A oportunidade criada à mão começa sem conversa.</p>
            ) : (
              <ul className="op-conversas">
                {d.conversas.map((c) => {
                  const sit = degrauDe(SITUACAO_DO_ATENDIMENTO, c.situacao);
                  return (
                    <li key={c.id}>
                      <Link to={`/comercial/conversa/${c.id}`}>{c.canal.nome}</Link>
                      <span className="op-aux">aberta em {data(c.abertaEm)} · última atividade {dataHora(c.ultimaAtividade)}</span>
                      <Selo tom={sit.tom}>{sit.texto}</Selo>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <section className="cartao op-cartao op-historico" aria-label="Histórico do cartão">
          <div className="cartao-cabeca">
            <h2>Histórico</h2>
            <span className="sub">cada movimento do funil, com quem moveu e por quê</span>
          </div>
          <ol className="op-historico-lista">
            {d.movimentos.map((m) => <ItemDoHistorico key={m.id} m={m} />)}
          </ol>
        </section>
      </div>

      <JanelaMover aberta={janela === 'mover'} aoFechar={() => setJanela(null)}
        oportunidade={{ id: o.id, estagio: o.estagio, contato: nomeDoContato(o.contato.nome) }} aoMover={() => feito('O cartão andou.')} />
      <JanelaProposta aberta={janela === 'proposta'} aoFechar={() => setJanela(null)}
        oportunidade={{ id: o.id, contato: nomeDoContato(o.contato.nome) }} aoGravar={() => feito('Proposta registrada.')} />
      <JanelaDaOportunidade aberta={janela === 'mudar'} d={d} aoFechar={() => setJanela(null)}
        aoGravar={() => feito('Oportunidade gravada.')} />
      <JanelaDaTarefa aberta={janela === 'tarefa'} oportunidade={o.id} contato={nomeDoContato(o.contato.nome)}
        aoFechar={() => setJanela(null)} aoGravar={() => feito('Tarefa criada.')} />
      <JanelaCancelarTarefa aberta={!!cancelando} tarefa={cancelando} aoFechar={() => setCancelando(null)}
        aoGravar={() => feito('Tarefa cancelada.')} />
    </div>
  );
}
